import { useMemo, useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { EndpointDetails } from '@kirin/api-client/contracts/endpoints';
import { Button } from '@/components/ui/button';
import { AgentVersionWithUpdate } from '@/components/shared/AgentVersionWithUpdate';
import { compareAgentVersions } from '@/components/shared/agentVersion';
import { useLatestReleases } from '@/hooks/api/useLatestReleases';
import { RelativeTimeWithTooltip } from '@/components/shared/RelativeTimeWithTooltip';
import { EndpointSearchInput } from '@/components/shared/EndpointSearchInput';
import { useEndpointSearch } from '@/components/shared/endpointSearch';

/**
 * KIR-3911: an endpoint row for the shared endpoints table. `occurrenceCount`
 * is present only in the Alerts context (drives the optional "Alerts" column).
 */
export type EndpointTableRow = EndpointDetails & {
  occurrenceCount?: number;
  // Present in the Alerts context (from groupedAlertEndpointSchema): the owner
  // of the (machine, user) bucket. Used for a sort-stable row key.
  userId?: string | null;
};

type SortField =
  | 'name'
  | 'agentVersion'
  | 'lastSeen'
  | 'osString'
  | 'ipAddresses'
  | 'macAddresses'
  | 'occurrenceCount'
  | 'machineId'
  | 'lastUpdateCheckAt'
  | 'lastUpdateError';

/** Comparable value for a sort field. Arrays (IP/MAC) join; nulls sort last. */
function sortKey(row: EndpointTableRow, field: SortField): string | number {
  if (field === 'occurrenceCount') return row.occurrenceCount ?? 0;
  const v = row[field];
  if (Array.isArray(v)) return v.join(',');
  return (v ?? '') as string;
}

function sortRows(
  rows: EndpointTableRow[],
  field: SortField,
  asc: boolean
): EndpointTableRow[] {
  const dir = asc ? 1 : -1;
  return [...rows].sort((a, b) => {
    // Agent version is semver, not lexical: 3.9.0 < 3.10.0 (matches the
    // backend's numeric least-version), else the header sort misleads.
    if (field === 'agentVersion') {
      return compareAgentVersions(a.agentVersion, b.agentVersion) * dir;
    }
    const ka = sortKey(a, field);
    const kb = sortKey(b, field);
    if (typeof ka === 'number' && typeof kb === 'number') {
      return (ka - kb) * dir;
    }
    return String(ka).localeCompare(String(kb)) * dir;
  });
}

/**
 * KIR-2962: drop link-local addresses (IPv6 `fe80::/10`, IPv4 `169.254.0.0/16`)
 * from the displayed IP list. They're non-routable noise for endpoint
 * identification, and the EUI-64 `fe80::` ones re-encode the MAC address that's
 * already shown in its own column — so hiding them de-clutters and avoids a
 * redundant MAC leak. Routable/LAN and unique-local (`fc00::/7`) addresses stay.
 */
function routableIps(ips: string[]): string[] {
  return ips.filter(ip => {
    const v = ip.trim().toLowerCase();
    if (/^fe[89ab][0-9a-f]:/.test(v)) return false; // IPv6 link-local fe80::/10
    if (v.startsWith('169.254.')) return false; // IPv4 link-local
    return true;
  });
}

const endpointOfRow = (
  row: EndpointTableRow
): { name: string | null; machineId: string } => ({
  name: row.name,
  machineId: row.machineId,
});

/** Render a string[] with a line break between entries (readability). */
function AddressList({ values }: { values: string[] }): ReactElement {
  if (!values.length) return <>—</>;
  return (
    <>
      {values.map((v, i) => (
        <div key={`${v}-${i}`} className="whitespace-nowrap">
          {v}
        </div>
      ))}
    </>
  );
}

interface EndpointsTableProps {
  endpoints: EndpointTableRow[];
  isLoading?: boolean;
  isError?: boolean;
  /** Add an "Alerts" column (per-endpoint occurrence count). Alerts context. */
  showAlerts?: boolean;
  /**
   * KIR-5191: show each machine's automatic-update state next to its version,
   * plus "Last update check" and "Update error" columns. Inventory → Users
   * dialog only, while the `agent_auto_update` flag is on.
   */
  showUpdateStatus?: boolean;
  /**
   * KIR-5191: adds a per-row "Try update now" that asks only that machine's
   * agent to check. Omitted, or for a machine pinned by MDM or without the
   * helper (it cannot update by itself), no button shows.
   */
  onTryUpdate?: (machineId: string) => void;
  /** Disables every per-row "Try update now" (a request is in flight). */
  tryUpdateDisabled?: boolean;
  /**
   * KIR-5412 (`shared_devices`): links each endpoint name to its endpoint
   * page. Omitted, the name is plain text as before.
   */
  getEndpointHref?: (machineId: string) => string;
}

/**
 * KIR-3911: the single endpoints table shared by the Inventory→Users endpoints
 * dialog and the Alerts "Show Endpoints" dialog. Column order (ticket):
 * Endpoint name, Agent version, Last seen, OS, IP Addresses, MAC Addresses,
 * [Alerts], Endpoint ID (last). IP/MAC render one-per-line; the agent version
 * uses {@link AgentVersionWithUpdate} — red + an amber "Update available" pill
 * when older than the real latest GA release (KIR-2962), same source as the
 * Users-tab cell; the endpoint id is plain text — no link to Inventory (link
 * removed per KIR-3911).
 */
export function EndpointsTable({
  endpoints,
  isLoading = false,
  isError = false,
  showAlerts = false,
  showUpdateStatus = false,
  onTryUpdate,
  tryUpdateDisabled = false,
  getEndpointHref,
}: EndpointsTableProps): ReactElement {
  const [sort, setSort] = useState<{ field: SortField; asc: boolean }>({
    field: 'lastSeen',
    asc: false,
  });

  // KIR-2962: freshness judged against the real latest GA release
  // (/api/releases/latest), same source as the Users-tab cell — so a version
  // ahead of GA isn't wrongly flagged as outdated.
  const { data: latestReleases } = useLatestReleases();
  const agentLatest = latestReleases?.agent.latestVersion ?? null;

  // KIR-5098: search the list by endpoint name or ID.
  const { query, setQuery, filtered, isFiltering } = useEndpointSearch(
    endpoints,
    endpointOfRow
  );
  const rows = useMemo(
    () => sortRows(filtered, sort.field, sort.asc),
    [filtered, sort]
  );

  const toggleSort = (field: SortField): void =>
    setSort(prev =>
      prev.field === field ? { field, asc: !prev.asc } : { field, asc: true }
    );

  const columns: { field: SortField; label: string }[] = [
    { field: 'name', label: 'Endpoint name' },
    { field: 'agentVersion', label: 'Agent version' },
    { field: 'lastSeen', label: 'Last seen' },
    { field: 'osString', label: 'OS' },
    { field: 'ipAddresses', label: 'IP Addresses' },
    { field: 'macAddresses', label: 'MAC Addresses' },
    ...(showUpdateStatus
      ? [
          {
            field: 'lastUpdateCheckAt' as SortField,
            label: 'Last update check',
          },
          { field: 'lastUpdateError' as SortField, label: 'Update error' },
        ]
      : []),
    ...(showAlerts
      ? [{ field: 'occurrenceCount' as SortField, label: 'Alerts' }]
      : []),
    { field: 'machineId', label: 'Endpoint ID' },
  ];
  const columnCount = columns.length + (onTryUpdate ? 1 : 0);

  return (
    <>
      {!isLoading && !isError && endpoints.length > 0 && (
        <EndpointSearchInput
          value={query}
          onChange={setQuery}
          className="mt-4"
        />
      )}
      <div className="rounded-md border border-table-border overflow-auto mt-4 max-h-[70vh]">
        <Table className="text-xs">
          <TableHeader className="sticky top-0 z-20 border-b bg-muted">
            <TableRow className="!bg-transparent hover:!bg-transparent">
              {columns.map(col => (
                <TableHead
                  key={col.label}
                  className="cursor-pointer select-none"
                  role="button"
                  tabIndex={0}
                  onClick={() => toggleSort(col.field)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleSort(col.field);
                    }
                  }}
                >
                  {col.label}
                  {sort.field === col.field ? (sort.asc ? ' ▲' : ' ▼') : ''}
                </TableHead>
              ))}
              {onTryUpdate && (
                <TableHead>
                  <span className="sr-only">Actions</span>
                </TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={columnCount}
                  className="text-center py-6 text-muted-foreground"
                >
                  Loading endpoints…
                </TableCell>
              </TableRow>
            ) : isError ? (
              <TableRow>
                <TableCell
                  colSpan={columnCount}
                  className="text-center py-6 text-muted-foreground"
                >
                  Failed to load endpoints
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columnCount}
                  className="text-center py-6 text-muted-foreground"
                >
                  {isFiltering
                    ? 'No endpoints match your search'
                    : 'No endpoints recorded'}
                </TableCell>
              </TableRow>
            ) : (
              rows.map(ep => (
                // Alerts context can carry the SAME machineId for two different
                // reporting users (backend buckets per (machine, user)), so pair
                // machineId with the owner. Keyed on data (not the array index) so
                // re-sorting reorders existing rows instead of remounting the body.
                <TableRow
                  key={`${ep.machineId}-${ep.userId ?? 'unknown'}`}
                  className="border-b hover:bg-muted/50"
                >
                  <TableCell>
                    {getEndpointHref ? (
                      <Link
                        to={getEndpointHref(ep.machineId)}
                        className="font-medium text-primary underline-offset-2 hover:underline"
                      >
                        {ep.name ?? ep.machineId}
                      </Link>
                    ) : (
                      (ep.name ?? '—')
                    )}
                  </TableCell>
                  <TableCell>
                    <AgentVersionWithUpdate
                      version={ep.agentVersion}
                      latestVersion={agentLatest}
                      updateState={showUpdateStatus ? ep.updateState : null}
                    />
                  </TableCell>
                  <TableCell>
                    {ep.lastSeen ? (
                      <RelativeTimeWithTooltip date={ep.lastSeen} />
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell>{ep.osString ?? '—'}</TableCell>
                  <TableCell>
                    <AddressList values={routableIps(ep.ipAddresses)} />
                  </TableCell>
                  <TableCell>
                    <AddressList values={ep.macAddresses} />
                  </TableCell>
                  {showUpdateStatus && (
                    <>
                      <TableCell>
                        {ep.lastUpdateCheckAt ? (
                          <RelativeTimeWithTooltip
                            date={ep.lastUpdateCheckAt}
                          />
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell className="max-w-60 break-words">
                        {ep.lastUpdateError ?? '—'}
                      </TableCell>
                    </>
                  )}
                  {showAlerts && (
                    <TableCell>{ep.occurrenceCount ?? 0}</TableCell>
                  )}
                  <TableCell className="font-mono">{ep.machineId}</TableCell>
                  {onTryUpdate && (
                    <TableCell>
                      {ep.updateState !== 'pinned_by_mdm' &&
                        ep.updateState !== 'not_available' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 whitespace-nowrap px-2 text-xs"
                            aria-label={`Try update now on ${ep.name ?? ep.machineId}`}
                            disabled={tryUpdateDisabled}
                            onClick={() => onTryUpdate(ep.machineId)}
                          >
                            Try update now
                          </Button>
                        )}
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </>
  );
}

EndpointsTable.displayName = 'EndpointsTable';
